import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorMessage } from '@/lib/api-error'
import { formatNumber } from '@/lib/money'

export interface BulkFailure<TItem> {
    item: TItem
    message: string
}

export interface BulkProgress {
    running: boolean
    total: number
    done: number
    succeeded: number
    failed: number
    /** Set when the operator asked to stop; the current item still completes. */
    cancelling: boolean
}

const IDLE: BulkProgress = { running: false, total: 0, done: 0, succeeded: 0, failed: 0, cancelling: false }

export interface RunBulkOptions<TItem> {
    items: TItem[]
    /** Rejecting counts as a failure for that item and does not stop the run. */
    action: (item: TItem) => Promise<void>
    /** Verb for the toasts, e.g. `'Refund'`. */
    label: string
    /** How many requests may be in flight. Keep at 1 for anything that moves money. */
    concurrency?: number
    /** Called once with the failures, so the caller can offer a retry of just those. */
    onSettled?: (result: { succeeded: TItem[]; failed: BulkFailure<TItem>[] }) => void
}

/**
 * Generalized from the withdrawals bulk runner, with two behaviour changes.
 *
 * The legacy runner reported only the first error, so a batch where twelve items
 * failed for twelve different reasons looked like one problem. This keeps every
 * failure and hands them back, which is what makes a partial retry possible.
 *
 * It is also cancellable. A sequential 200-item refund run previously had no way to
 * stop once started.
 */
export function useBulkRunner<TItem>() {
    const [progress, setProgress] = useState<BulkProgress>(IDLE)
    const cancelRef = useRef(false)

    const cancel = useCallback(() => {
        cancelRef.current = true
        setProgress(current => ({ ...current, cancelling: true }))
    }, [])

    const run = useCallback(async ({ items, action, label, concurrency = 1, onSettled }: RunBulkOptions<TItem>) => {
        if (items.length === 0) return { succeeded: [], failed: [] }

        cancelRef.current = false
        setProgress({ running: true, total: items.length, done: 0, succeeded: 0, failed: 0, cancelling: false })

        const succeeded: TItem[] = []
        const failed: BulkFailure<TItem>[] = []
        let cursor = 0

        const worker = async () => {
            while (cursor < items.length && !cancelRef.current) {
                const item = items[cursor++]!
                try {
                    await action(item)
                    succeeded.push(item)
                } catch (error) {
                    failed.push({ item, message: getApiErrorMessage(error, `${label} failed.`) })
                }
                setProgress(current => ({
                    ...current,
                    done: current.done + 1,
                    succeeded: succeeded.length,
                    failed: failed.length
                }))
            }
        }

        await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker))

        const skipped = items.length - succeeded.length - failed.length
        setProgress(IDLE)

        if (failed.length === 0 && skipped === 0) {
            toast.success(`${label}: ${formatNumber(succeeded.length)} succeeded`)
        } else if (succeeded.length === 0) {
            toast.error(`${label} failed`, {
                description: failed[0]?.message ?? 'No items were processed.'
            })
        } else {
            // Distinct failure reasons are the useful signal: "12 failed" with one
            // message hides eleven different problems.
            const reasons = [...new Set(failed.map(entry => entry.message))]
            toast.warning(
                `${label}: ${formatNumber(succeeded.length)} succeeded, ${formatNumber(failed.length)} failed${
                    skipped > 0 ? `, ${formatNumber(skipped)} skipped` : ''
                }`,
                {
                    description:
                        reasons.length === 1 ? reasons[0] : `${reasons[0]} (and ${reasons.length - 1} other reasons)`,
                    duration: 10_000
                }
            )
        }

        onSettled?.({ succeeded, failed })
        return { succeeded, failed }
    }, [])

    return { progress, run, cancel }
}
